const defaultOptions = {
	sm: 576,
	md: 768,
	lg: 992,
	xl: 1220,
	options: {
		polyfill: true,
		throttle: 300
	}
};

const defaultBreakpoints = {
	current: 'xs',

	xs: false,

	sm: false,
	lSm: false,
	sSm: false,

	md: false,
	lMd: false,
	sMd: false,

	lg: false,
	lLg: false,
	sLg: false,

	sXl: false,
	xl: false,

	width: 0,
	height: 0
};

function transformBreakpoints (breakpoints, { width, height }, opts) {
	const { sm, md, lg, xl } = opts;
	const roles = { xs: 0, sm: 1, md: 2, lg: 3, xl: 4 };

	let currentActive = 'xs';
	switch (true) {
		case width >= xl: currentActive = 'xl'; break;
		case width >= lg: currentActive = 'lg'; break;
		case width >= md: currentActive = 'md'; break;
		case width >= sm: currentActive = 'sm'; break;
		default: currentActive = 'xs'; break;
	}

	const decideLargeOrSmallThan = (point, large = false) =>
		large ? roles[point] < roles[currentActive] : roles[point] > roles[currentActive];

	const next = {
		sm: currentActive === 'sm',
		lSm: decideLargeOrSmallThan('sm', true),
		sSm: decideLargeOrSmallThan('sm'),

		md: currentActive === 'md',
		lMd: decideLargeOrSmallThan('md', true),
		sMd: decideLargeOrSmallThan('md'),

		lg: currentActive === 'lg',
		lLg: decideLargeOrSmallThan('lg', true),
		sLg: decideLargeOrSmallThan('lg'),

		xl: currentActive === 'xl',
		sXl: decideLargeOrSmallThan('xl')
	};

	Object.assign(breakpoints, next, {
		current: currentActive,
		xs: currentActive === 'xs',
		width: ~~width,
		height: ~~height
	});
}

/**
 * Vue 3 Breakpoints Plugin (document/ResizeObserver only)
 *
 * Usage:
 *   import { createApp } from 'vue'
 *   import Breakpoints from './breakpoints-plugin'
 *   const app = createApp(App)
 *   app.use(Breakpoints, { sm: 640, md: 768, lg: 1024, xl: 1280 }) // optional overrides
 *   // in components (Options API): this.$breakpoints.current
 *   // in components (Composition API): const bp = inject('breakpoints')
 */
export default {
	install (app, userOptions = {}) {
		const opts = {
			...defaultOptions,
			...userOptions,
			options: { ...defaultOptions.options, ...(userOptions.options || {}) }
		};

		const breakpoints = app.reactive
			? app.reactive({ ...defaultBreakpoints }) // Vue macro envs sometimes pass app.reactive
			: (typeof reactive !== 'undefined' ? reactive({ ...defaultBreakpoints }) : { ...defaultBreakpoints });

		// Helper to read current viewport/body rect safely
		const readSize = () => {
			if (typeof document === 'undefined') {
				return { width: 0, height: 0 };
			}
			const body = document.body || document.documentElement;
			// Prefer body’s content box (matches what RO will emit)
			const rect = body.getBoundingClientRect();
			const width = rect?.width || document.documentElement.clientWidth || window.innerWidth || 0;
			const height = rect?.height || document.documentElement.clientHeight || window.innerHeight || 0;
			return { width, height };
		};

		const init = () => {
			// Initial compute from current document size
			transformBreakpoints(breakpoints, readSize(), opts);

			if (typeof window === 'undefined') {
				return;
			}

			const bodyElem = document.querySelector('body');
			if (!bodyElem) {
				return;
			}

			const onResize = throttle((entries) => {
				const [{ contentRect }] = entries;
				transformBreakpoints(breakpoints, contentRect, opts);
			}, +opts.options.throttle, { trailing: true, leading: false });

			const resizeObserver = new window.ResizeObserver(onResize);
			resizeObserver.observe(bodyElem);

			// Fallback: also handle window resize to catch edge cases where RO might not fire
			const winResize = () => transformBreakpoints(breakpoints, readSize(), opts);
			window.addEventListener('resize', winResize);

			// Expose a simple teardown in case the host app wants to manually clean up
			app.provide('breakpoints:teardown', () => {
				try {
					resizeObserver.disconnect();
				} catch {}
				window.removeEventListener('resize', winResize);
			});
		};

		if (typeof window !== 'undefined') {
			init();
		}

		// Provide & attach to globalProperties
		// app.provide('breakpoints', breakpoints)
		if (app.config?.globalProperties) {
			console.log(breakpoints);
			app.config.globalProperties.$breakpoints = breakpoints;
		}
	}
};


function throttle (fn, wait, options = {}) {
	let timeout = null;
	let lastArgs;
	let lastCallTime = 0;
	const { leading = false, trailing = true } = options;

	const invoke = (time) => {
		lastCallTime = time;
		fn.apply(null, lastArgs);
		lastArgs = null;
	};

	const throttled = function (...args) {
		const now = Date.now();
		if (!lastCallTime && !leading) {
			lastCallTime = now;
		}

		const remaining = wait - (now - lastCallTime);
		lastArgs = args;

		if (remaining <= 0 || remaining > wait) {
			if (timeout) {
				clearTimeout(timeout);
				timeout = null;
			}
			invoke(now);
		} else if (!timeout && trailing) {
			timeout = setTimeout(() => {
				timeout = null;
				invoke(Date.now());
			}, remaining);
		}
	};

	throttled.cancel = () => {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = null;
		lastArgs = null;
		lastCallTime = 0;
	};

	return throttled;
}
